// app/shared/components/markdown/CodeBlockComponent.tsx
"use client";

import { Check, Copy } from "lucide-react";
import { useState } from "react";

interface CodeBlockProps {
  code: string;
}

export const CodeBlockComponent = ({ code }: CodeBlockProps) => {
  const [copyStatus, setCopyStatus] = useState<"copy" | "copied" | "error">(
    "copy",
  );

  const handleCopy = async () => {
    if (!code) {
      console.warn("No code to copy");
      return;
    }

    try {
      // Check if clipboard API is available
      if (!navigator.clipboard) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = code;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand("copy");
          setCopyStatus("copied");
          console.log("Code copied using fallback method");
        } catch (err) {
          console.error("Fallback copy failed:", err);
          setCopyStatus("error");
        } finally {
          document.body.removeChild(textArea);
        }
      } else {
        // Modern clipboard API
        await navigator.clipboard.writeText(code);
        setCopyStatus("copied");
        console.log("Code copied to clipboard:", code.substring(0, 50) + "...");
      }

      // Reset status after 2 seconds
      setTimeout(() => {
        setCopyStatus("copy");
      }, 2000);
    } catch (error) {
      console.error("Failed to copy code:", error);
      setCopyStatus("error");
      setTimeout(() => {
        setCopyStatus("copy");
      }, 2000);
    }
  };

  const getButtonContent = () => {
    switch (copyStatus) {
      case "copied":
        return (
          <>
            <Check size={16} />
            <span>Copied!</span>
          </>
        );
      case "error":
        return (
          <>
            <Copy size={16} />
            <span>Failed</span>
          </>
        );
      default:
        return (
          <>
            <Copy size={16} />
            <span>Copy</span>
          </>
        );
    }
  };

  const getButtonClasses = () => {
    const baseClasses =
      "absolute top-2 right-2 px-3 py-1.5 rounded-md opacity-90 hover:opacity-100 transition-all duration-200 text-xs flex items-center gap-1.5 font-medium cursor-pointer border z-10";

    switch (copyStatus) {
      case "copied":
        return `${baseClasses} bg-green-600 text-white border-green-500 shadow-lg`;
      case "error":
        return `${baseClasses} bg-red-600 text-white border-red-500 shadow-lg`;
      default:
        return `${baseClasses} bg-gray-800 text-white border-gray-700 hover:bg-gray-700 hover:border-gray-600 shadow-lg`;
    }
  };

  return (
    <div className="relative group my-4">
      <pre className="p-4 bg-[var(--color-beige-dark)] text-gray-800 rounded-md overflow-x-auto text-sm border border-[var(--color-beige)] font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className={getButtonClasses()}
        disabled={copyStatus !== "copy"}
        title={
          copyStatus === "copy"
            ? "Copy code to clipboard"
            : copyStatus === "copied"
              ? "Copied!"
              : "Copy failed"
        }
      >
        {getButtonContent()}
      </button>
    </div>
  );
};
